/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @fileoverview App utility module that provides functions for loading different application variants
 * based on application type constants. Handles all mobile and desktop application variants.
 * @module utils/app
 */

import React from "react";

import {
  APPLICATION_TYPE_AMERICAN_DESKTOP,
  APPLICATION_TYPE_ASIAN_DESKTOP,
  APPLICATION_TYPE_CITY_DESKTOP,
  APPLICATION_TYPE_COMPACT_DESKTOP,
  APPLICATION_TYPE_CONTINENTAL_DESKTOP,
  APPLICATION_TYPE_EUROPEAN_DESKTOP,
  APPLICATION_TYPE_MOBILE_AMERICAN,
  APPLICATION_TYPE_MOBILE_ASIAN,
  APPLICATION_TYPE_MOBILE_CITY,
  APPLICATION_TYPE_MOBILE_SLIM,
  APPLICATION_TYPE_MOBILE_SUMMIT,
  APPLICATION_TYPE_MOBILE_VANILLA,
  APPLICATION_TYPE_SLIM_DESKTOP,
  APPLICATION_TYPE_SUMMIT_DESKTOP,
} from "constants/application-types";

/**
 * IMPORTANT: All application components must be lazy loaded to prevent CSS conflicts
 * between different application variants.
 * @see https://reactjs.org/docs/code-splitting.html
 */
const VanillaMobileApp = React.lazy(() => import("../applications/vanillamobile/VanillaMobileApp"));
const CityDesktopApp = React.lazy(() => import("../applications/citydesktop/CityDesktopApp"));
const VanillaDesktopApp = React.lazy(() => import("../applications/vanilladesktop/VanillaDesktopApp"));
const CityMobileApp = React.lazy(() => import("../applications/citymobile/CityMobileApp"));
const SlimApp = React.lazy(() => import("../applications/slim/SlimApp"));
const SummitApp = React.lazy(() => import("../applications/summit/SummitApp"));
const LatamApp = React.lazy(() => import("../applications/latam/LatamApp"));

/**
 * Returns the appropriate mobile application component based on the provided application type.
 * Falls back to SlimApp if no matching application type is found.
 *
 * @function
 * @param {string} mobileApplicationType - The mobile application type constant
 * @returns {React.LazyExoticComponent<React.ComponentType>} The lazy-loaded mobile application component
 */
export function getAppMobile(mobileApplicationType) {
  return (
    {
      [APPLICATION_TYPE_MOBILE_ASIAN]: VanillaMobileApp,
      [APPLICATION_TYPE_MOBILE_CITY]: CityMobileApp,
      [APPLICATION_TYPE_MOBILE_SLIM]: SlimApp,
      [APPLICATION_TYPE_MOBILE_SUMMIT]: SummitApp,
      [APPLICATION_TYPE_MOBILE_VANILLA]: VanillaMobileApp,
      [APPLICATION_TYPE_MOBILE_AMERICAN]: LatamApp,
    }[mobileApplicationType] ?? SlimApp
  );
}

/**
 * Returns the appropriate desktop application component based on the provided application type.
 * Falls back to VanillaDesktopApp if no matching application type is found.
 *
 * @function
 * @param {string} desktopApplicationType - The desktop application type constant
 * @returns {React.LazyExoticComponent<React.ComponentType>} The lazy-loaded desktop application component
 */
export function getAppDesktop(desktopApplicationType) {
  return (
    {
      [APPLICATION_TYPE_AMERICAN_DESKTOP]: LatamApp,
      [APPLICATION_TYPE_ASIAN_DESKTOP]: VanillaDesktopApp,
      [APPLICATION_TYPE_CITY_DESKTOP]: CityDesktopApp,
      [APPLICATION_TYPE_COMPACT_DESKTOP]: VanillaDesktopApp,
      [APPLICATION_TYPE_CONTINENTAL_DESKTOP]: VanillaDesktopApp,
      [APPLICATION_TYPE_EUROPEAN_DESKTOP]: VanillaDesktopApp,
      [APPLICATION_TYPE_SLIM_DESKTOP]: SlimApp,
      [APPLICATION_TYPE_SUMMIT_DESKTOP]: SummitApp,
    }[desktopApplicationType] ?? VanillaDesktopApp
  );
}
